<template>
  <div class="restaurant-riders-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">
    <div class="riders-page">

      <!-- Header -->
      <div class="page-header">
        <div class="header-left">
          <h1 class="page-title">🛵 Delivery Riders</h1>
          <p class="page-subtitle">Manage your rider pool. Riders receive new orders in their Telegram group.</p>
        </div>
        <div class="header-actions">
          <a href="https://api.quali-d.com/restaurant/rider-setup-guide" target="_blank" class="btn btn-ghost">
            📖 Setup Guide
          </a>
          <button class="btn btn-primary" @click="showAddModal = true">
            + Add Rider
          </button>
        </div>
      </div>

      <!-- Stats bar -->
      <div class="stats-bar" v-if="riders.length">
        <div class="stat-chip">
          <span class="stat-num">{{ activeRiders }}</span>
          <span class="stat-label">Active</span>
        </div>
        <div class="stat-chip">
          <span class="stat-num">{{ busyRiders }}</span>
          <span class="stat-label">On Delivery</span>
        </div>
        <div class="stat-chip">
          <span class="stat-num">{{ totalDeliveriesToday }}</span>
          <span class="stat-label">Deliveries Today</span>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading-state" v-if="loading">
        <div class="spinner"></div>
        <p>Loading riders…</p>
      </div>

      <!-- Empty state -->
      <div class="empty-state" v-else-if="!riders.length">
        <div class="empty-icon">🛵</div>
        <h3>No riders yet</h3>
        <p>Add your first delivery rider to start dispatching orders automatically.</p>
        <button class="btn btn-primary" @click="showAddModal = true">Add First Rider</button>
        <a href="https://api.quali-d.com/restaurant/rider-setup-guide" target="_blank" class="setup-link">
          Not sure how to set up? Read the setup guide →
        </a>
      </div>

      <!-- Riders table -->
      <div class="riders-table-wrap" v-else>
        <table class="riders-table">
          <thead>
            <tr>
              <th>Rider</th>
              <th>Phone</th>
              <th>Telegram ID</th>
              <th>Status</th>
              <th>Today</th>
              <th>Last Delivery</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rider in riders" :key="rider.id" :class="{ 'row-inactive': !rider.is_active }">
              <td class="rider-name-cell">
                <div class="rider-avatar">{{ initials(rider.full_name) }}</div>
                <div>
                  <div class="rider-name">{{ rider.full_name }}</div>
                  <div class="rider-username" v-if="rider.telegram_username">@{{ rider.telegram_username }}</div>
                </div>
              </td>
              <td>{{ rider.phone || '—' }}</td>
              <td class="mono">
                <span v-if="rider.telegram_user_id">{{ rider.telegram_user_id }}</span>
                <span v-else class="badge badge-pending">⏳ Awaiting</span>
              </td>
              <td>
                <span class="badge" :class="statusClass(rider)">{{ statusLabel(rider) }}</span>
              </td>
              <td class="mono">{{ rider.daily_order_count || 0 }}</td>
              <td>{{ formatRelative(rider.last_delivered_at) }}</td>
              <td class="actions-cell">
                <button
                  v-if="!rider.telegram_user_id && rider.invite_link"
                  class="action-btn"
                  @click="copyInviteLink(rider.invite_link)"
                  title="Copy invite link"
                >📋</button>
                <button class="action-btn" @click="openEdit(rider)" title="Edit">✏️</button>
                <button
                  class="action-btn"
                  @click="toggleActive(rider)"
                  :title="rider.is_active ? 'Deactivate' : 'Activate'"
                >{{ rider.is_active ? '🔴' : '🟢' }}</button>
                <button class="action-btn danger" @click="confirmRemove(rider)" title="Remove">🗑️</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Add Rider Modal -->
      <div class="modal-overlay" v-if="showAddModal" @click.self="closeAddModal">
        <div class="modal">
          <div class="modal-header">
            <h2>{{ newInviteLink ? '🎉 Rider Added' : 'Add Rider' }}</h2>
            <button class="modal-close" @click="closeAddModal">✕</button>
          </div>
          <div class="modal-body">
            <!-- Success: show invite link to share -->
            <div v-if="newInviteLink" class="invite-panel">
              <p>Share this link with <strong>{{ addForm.full_name }}</strong> on WhatsApp or SMS. When they tap it and press Start in Telegram, they'll be automatically registered.</p>
              <div class="invite-link-row">
                <input class="invite-link-input" readonly :value="newInviteLink" />
                <button class="btn btn-primary" @click="copyInviteLink(newInviteLink)">
                  {{ copied ? '✅ Copied!' : '📋 Copy' }}
                </button>
              </div>
            </div>

            <!-- Form fields -->
            <template v-else>
              <div class="form-group">
                <label>Full Name *</label>
                <input v-model="addForm.full_name" placeholder="Ahmed Mohamed" class="form-input" />
              </div>
              <div class="form-group">
                <label>Phone Number (optional)</label>
                <input v-model="addForm.phone" placeholder="01xxxxxxxxx" class="form-input" />
              </div>
              <div class="field-hint">
                💡 After adding, you'll get a one-tap invite link to send the rider. They tap it in Telegram and are registered instantly — no manual ID needed.
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button class="btn btn-ghost" @click="closeAddModal">{{ newInviteLink ? 'Done' : 'Cancel' }}</button>
            <button v-if="!newInviteLink" class="btn btn-primary" :disabled="addLoading" @click="addRider">
              {{ addLoading ? 'Adding…' : 'Add & Get Invite Link' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Edit Rider Modal -->
      <div class="modal-overlay" v-if="editRider" @click.self="editRider = null">
        <div class="modal">
          <div class="modal-header">
            <h2>Edit Rider</h2>
            <button class="modal-close" @click="editRider = null">✕</button>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label>Full Name</label>
              <input v-model="editForm.full_name" class="form-input" />
            </div>
            <div class="form-group">
              <label>Phone Number</label>
              <input v-model="editForm.phone" class="form-input" />
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-ghost" @click="editRider = null">Cancel</button>
            <button class="btn btn-primary" :disabled="editLoading" @click="saveEdit">
              {{ editLoading ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Remove confirm modal -->
      <div class="modal-overlay" v-if="removeTarget" @click.self="removeTarget = null">
        <div class="modal modal-sm">
          <div class="modal-header">
            <h2>Remove Rider</h2>
            <button class="modal-close" @click="removeTarget = null">✕</button>
          </div>
          <div class="modal-body">
            <p>Remove <strong>{{ removeTarget.full_name }}</strong> from the rider pool?</p>
            <p class="hint-text">They won't receive new orders. You can re-add them later.</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-ghost" @click="removeTarget = null">Cancel</button>
            <button class="btn btn-danger" :disabled="removeLoading" @click="removeRider">
              {{ removeLoading ? 'Removing…' : 'Remove' }}
            </button>
          </div>
        </div>
      </div>

    </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'

const API = 'https://api.quali-d.com'

export default {
  name: 'RestaurantRidersView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      riders: [],
      loading: true,
      showAddModal: false,
      addLoading: false,
      newInviteLink: null,
      copied: false,
      addForm: {
        full_name: '',
        phone: ''
      },
      editRider: null,
      editForm: { full_name: '', phone: '' },
      editLoading: false,
      removeTarget: null,
      removeLoading: false
    }
  },

  computed: {
    activeRiders () { return this.riders.filter(r => r.is_active).length },
    busyRiders () { return this.riders.filter(r => r.active_orders > 0).length },
    totalDeliveriesToday () {
      return this.riders.reduce((s, r) => s + (parseInt(r.daily_order_count) || 0), 0)
    }
  },

  mounted () {
    this.fetchRiders()
  },

  methods: {
    async fetchRiders () {
      this.loading = true
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(`${API}/restaurant-mode/riders`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data.success) this.riders = data.riders
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    async addRider () {
      if (!this.addForm.full_name) {
        alert('Rider name is required.')
        return
      }
      this.addLoading = true
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(`${API}/restaurant-mode/riders`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.addForm)
        })
        const data = await res.json()
        if (data.success) {
          await this.fetchRiders()
          if (data.invite_link) {
            this.newInviteLink = data.invite_link
          } else {
            this.closeAddModal()
          }
        } else {
          alert(data.message || 'Failed to add rider.')
        }
      } catch (e) {
        alert('Error adding rider.')
      } finally {
        this.addLoading = false
      }
    },

    closeAddModal () {
      this.showAddModal = false
      this.newInviteLink = null
      this.copied = false
      this.addForm = { full_name: '', phone: '' }
    },

    copyInviteLink (link) {
      navigator.clipboard.writeText(link).then(() => {
        this.copied = true
        setTimeout(() => { this.copied = false }, 2500)
      }).catch(() => {
        prompt('Copy this link:', link)
      })
    },

    openEdit (rider) {
      this.editRider = rider
      this.editForm = { full_name: rider.full_name, phone: rider.phone || '' }
    },

    async saveEdit () {
      this.editLoading = true
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(`${API}/restaurant-mode/riders/${this.editRider.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...this.editForm, is_active: this.editRider.is_active })
        })
        const data = await res.json()
        if (data.success) {
          this.editRider = null
          await this.fetchRiders()
        }
      } catch (e) {
        alert('Error saving changes.')
      } finally {
        this.editLoading = false
      }
    },

    async toggleActive (rider) {
      try {
        const token = localStorage.getItem('auth_token')
        await fetch(`${API}/restaurant-mode/riders/${rider.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            full_name: rider.full_name,
            phone: rider.phone,
            is_active: rider.is_active ? 0 : 1
          })
        })
        await this.fetchRiders()
      } catch (e) {
        alert('Error updating rider.')
      }
    },

    confirmRemove (rider) { this.removeTarget = rider },

    async removeRider () {
      this.removeLoading = true
      try {
        const token = localStorage.getItem('auth_token')
        const res = await fetch(`${API}/restaurant-mode/riders/${this.removeTarget.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (data.success) {
          this.removeTarget = null
          await this.fetchRiders()
        }
      } catch (e) {
        alert('Error removing rider.')
      } finally {
        this.removeLoading = false
      }
    },

    initials (name) {
      return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    },

    statusLabel (rider) {
      if (!rider.telegram_user_id) return 'Pending'
      if (!rider.is_active) return 'Inactive'
      if (rider.active_orders > 0) return 'On Delivery'
      return 'Available'
    },

    statusClass (rider) {
      if (!rider.telegram_user_id) return 'badge-pending'
      if (!rider.is_active) return 'badge-grey'
      if (rider.active_orders > 0) return 'badge-orange'
      return 'badge-green'
    },

    formatRelative (dt) {
      if (!dt) return '—'
      const diff = Math.floor((Date.now() - new Date(dt)) / 60000)
      if (diff < 1) return 'just now'
      if (diff < 60) return `${diff}m ago`
      if (diff < 1440) return `${Math.floor(diff / 60)}h ago`
      return new Date(dt).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.restaurant-riders-layout {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.page-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}
.riders-page { padding: 24px; max-width: 1100px; margin: 0 auto; }

.page-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 24px; gap: 16px; flex-wrap: wrap;
}
.page-title   { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.page-subtitle { font-size: 14px; color: #666; margin: 0; }
.header-actions { display: flex; gap: 10px; align-items: center; flex-shrink: 0; }

/* Stats bar */
.stats-bar { display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
.stat-chip {
  background: #f5f6fa; border-radius: 12px; padding: 12px 20px;
  display: flex; flex-direction: column; align-items: center; min-width: 90px;
}
.stat-num   { font-size: 22px; font-weight: 700; color: #333; }
.stat-label { font-size: 12px; color: #888; margin-top: 2px; }

/* Loading */
.loading-state { text-align: center; padding: 60px 20px; color: #888; }
.spinner {
  width: 36px; height: 36px; border: 3px solid #eee;
  border-top-color: #4f46e5; border-radius: 50%;
  animation: spin .7s linear infinite; margin: 0 auto 16px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Empty state */
.empty-state { text-align: center; padding: 60px 20px; }
.empty-icon  { font-size: 56px; margin-bottom: 16px; }
.empty-state h3 { font-size: 20px; margin: 0 0 8px; }
.empty-state p  { color: #666; margin: 0 0 20px; }
.setup-link { display: block; margin-top: 14px; color: #4f46e5; font-size: 14px; }

/* Table */
.riders-table-wrap { overflow-x: auto; background: #fff; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,.08); }
.riders-table { width: 100%; border-collapse: collapse; }
.riders-table th {
  padding: 12px 16px; font-size: 12px; font-weight: 600;
  color: #888; text-transform: uppercase; letter-spacing: .5px;
  border-bottom: 1px solid #eee; text-align: left;
}
.riders-table td { padding: 14px 16px; border-bottom: 1px solid #f0f0f0; font-size: 14px; vertical-align: middle; }
.riders-table tr:last-child td { border-bottom: none; }
.row-inactive { opacity: .55; }

.rider-name-cell { display: flex; align-items: center; gap: 12px; }
.rider-avatar {
  width: 38px; height: 38px; border-radius: 50%; background: #4f46e5;
  color: #fff; font-size: 14px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.rider-name     { font-weight: 600; }
.rider-username { font-size: 12px; color: #888; }
.mono { font-family: monospace; font-size: 13px; }

/* Badges */
.badge { padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
.badge-green   { background: #d1fae5; color: #065f46; }
.badge-orange  { background: #ffedd5; color: #9a3412; }
.badge-grey    { background: #f3f4f6; color: #6b7280; }
.badge-pending { background: #fef3c7; color: #92400e; }

/* Action buttons */
.actions-cell { display: flex; gap: 6px; }
.action-btn {
  background: none; border: 1px solid #e5e7eb; border-radius: 7px;
  padding: 5px 9px; font-size: 15px; cursor: pointer; transition: background .15s;
}
.action-btn:hover        { background: #f5f6fa; }
.action-btn.danger:hover { background: #fee2e2; border-color: #fca5a5; }

/* Buttons */
.btn {
  padding: 9px 18px; border-radius: 8px; font-size: 14px; font-weight: 600;
  cursor: pointer; border: none; transition: opacity .15s;
}
.btn:disabled { opacity: .6; cursor: not-allowed; }
.btn-primary { background: #4f46e5; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #4338ca; }
.btn-ghost   { background: #f3f4f6; color: #374151; }
.btn-ghost:hover { background: #e5e7eb; }
.btn-danger  { background: #ef4444; color: #fff; }
.btn-danger:hover:not(:disabled) { background: #dc2626; }

/* Modal */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display: flex; align-items: center; justify-content: center; z-index: 999; padding: 20px;
}
.modal {
  background: #fff; border-radius: 16px; width: 100%; max-width: 460px;
  box-shadow: 0 20px 60px rgba(0,0,0,.2);
}
.modal-sm { max-width: 380px; }
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px 0; margin-bottom: 16px;
}
.modal-header h2 { font-size: 18px; font-weight: 700; margin: 0; }
.modal-close {
  background: none; border: none; font-size: 18px;
  cursor: pointer; color: #888; padding: 4px;
}
.modal-body    { padding: 0 24px 8px; }
.modal-footer  { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px 20px; }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; color: #374151; }
.form-input {
  width: 100%; padding: 9px 12px; border: 1px solid #d1d5db;
  border-radius: 8px; font-size: 14px; box-sizing: border-box;
  outline: none; transition: border-color .15s;
}
.form-input:focus { border-color: #4f46e5; }

.field-hint {
  background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px;
  padding: 10px 14px; font-size: 13px; color: #1e40af; margin-bottom: 0; margin-top: 8px;
}
.field-hint a { color: #1d4ed8; font-weight: 600; }
.field-note   { font-size: 11px; color: #9ca3af; margin-top: 4px; display: block; }
.hint-text    { font-size: 13px; color: #6b7280; margin-top: 6px; }

/* Invite panel (shown after rider is created) */
.invite-panel {
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 16px;
}
.invite-panel p { font-size: 13px; color: #166534; margin: 0 0 12px; line-height: 1.5; }
.invite-link-row { display: flex; gap: 8px; align-items: center; }
.invite-link-input {
  flex: 1; font-size: 12px; font-family: monospace; padding: 8px 10px;
  border: 1px solid #d1d5db; border-radius: 6px; background: #fff; color: #111;
  min-width: 0;
}

/* ── Dark mode ─────────────────────────────────────────────────── */
@media (prefers-color-scheme: dark) {
  .restaurant-riders-layout { background: #111827; }

  .page-title   { color: #f9fafb; }
  .page-subtitle { color: #9ca3af; }

  /* Stats */
  .stat-chip  { background: #1f2937; }
  .stat-num   { color: #f9fafb; }
  .stat-label { color: #6b7280; }

  /* Loading / empty */
  .loading-state { color: #6b7280; }
  .spinner { border-color: #374151; border-top-color: #6366f1; }
  .empty-state h3 { color: #f9fafb; }
  .empty-state p  { color: #9ca3af; }
  .setup-link { color: #818cf8; }

  /* Table */
  .riders-table-wrap { background: #1f2937; box-shadow: 0 1px 4px rgba(0,0,0,.4); }
  .riders-table th { color: #6b7280; border-bottom-color: #374151; }
  .riders-table td { border-bottom-color: #374151; color: #d1d5db; }
  .rider-name     { color: #f9fafb; }
  .rider-username { color: #6b7280; }

  /* Badges */
  .badge-green   { background: #064e3b; color: #6ee7b7; }
  .badge-orange  { background: #431407; color: #fdba74; }
  .badge-grey    { background: #374151; color: #9ca3af; }
  .badge-pending { background: #451a03; color: #fcd34d; }

  /* Action buttons */
  .action-btn { border-color: #374151; color: #d1d5db; }
  .action-btn:hover        { background: #374151; }
  .action-btn.danger:hover { background: rgba(239,68,68,.15); border-color: #ef4444; }

  /* Header buttons */
  .btn-ghost { background: #374151; color: #d1d5db; }
  .btn-ghost:hover { background: #4b5563; }

  /* Modal */
  .modal-overlay { background: rgba(0,0,0,.65); }
  .modal { background: #1f2937; box-shadow: 0 20px 60px rgba(0,0,0,.6); }
  .modal-header h2 { color: #f9fafb; }
  .modal-close { color: #9ca3af; }
  .modal-close:hover { background: #374151; color: #f9fafb; }
  .modal-footer { border-top: 1px solid #374151; }
  .modal-header { border-bottom: 1px solid #374151; }

  /* Form */
  .form-group label { color: #d1d5db; }
  .form-input {
    background: #374151; border-color: #4b5563;
    color: #f9fafb;
  }
  .form-input:focus { border-color: #6366f1; }
  .form-input::placeholder { color: #6b7280; }

  /* Info boxes */
  .field-hint { background: #1e3a5f; border-color: #1d4ed8; color: #93c5fd; }
  .field-hint a { color: #60a5fa; }
  .field-note { color: #6b7280; }
  .hint-text  { color: #9ca3af; }

  /* Invite panel */
  .invite-panel { background: #052e16; border-color: #166534; }
  .invite-panel p { color: #86efac; }
  .invite-link-input { background: #1f2937; border-color: #374151; color: #f9fafb; }

  /* Confirm modal text */
  .modal-body p { color: #d1d5db; }
  .hint-text    { color: #9ca3af; }
}
</style>
